import { BrowseArtefactsModalContent } from '@modules/bookmark/components/BrowseArtefactsModalContent';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { DashboardList } from '@modules/dashboard/components/DashboardList';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { SAVED_ARTEFACTS_MODAL_ID } from '@modules/leftNavSidebar/components/SavedNavItem/constants';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { Stack, Typography } from '@mui/material';
import {
  ArtefactEditableByFilter,
  ArtefactType,
  DashboardListItemViewFragment,
  DashboardUseCase,
} from 'generated/graphql';

interface OtherUsersDashboardsListProps {
  dashboards: DashboardListItemViewFragment[] | undefined;
  onClose: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

export const OtherUsersDashboardsList = ({
  dashboards,
  onClose,
  hasMore,
  isLoading,
}: OtherUsersDashboardsListProps) => {
  const { openModal } = useMuiModalContext();

  const onSeeAllClick = () => {
    trackDashboardEvent({
      eventName: 'See All Other Dashboards Clicked',
    });
    openModal({
      contentComponent: () => (
        <BrowseArtefactsModalContent
          artefactTypes={[
            {
              type: ArtefactType.Dashboard,
              useCase: DashboardUseCase.Default,
            },
          ]}
          editableBy={ArtefactEditableByFilter.Others}
        />
      ),
      modalProps: {
        id: SAVED_ARTEFACTS_MODAL_ID,
        fullWidth: true,
        maxWidth: 'lg',
        PaperProps: {
          sx: { width: '1000px', height: '600px', borderRadius: 2 },
        },
      },
    });
  };

  const renderDashboardList = () => {
    if (isLoading) {
      return (
        <Stack
          py={2}
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
        >
          <Typography variant="caption" color="text.disabled">
            Loading...
          </Typography>
          <CircularLoader size={16} />
        </Stack>
      );
    }

    if (!dashboards?.length) {
      return (
        <Stack p={1} pl={2.5}>
          <Typography variant="caption" color="text.disabled">
            There are no dashboards to discover yet!
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Invite your teammates to Enterpret to help them get started!
          </Typography>
        </Stack>
      );
    }

    return (
      <DashboardList
        dashboards={dashboards}
        hasMore={hasMore}
        onSeeAllClick={onSeeAllClick}
        onClose={onClose}
        testID="other-users-dashboards-list"
      />
    );
  };

  return (
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <TravelExploreRoundedIcon sx={{ fontSize: '12px' }} />
        <Typography variant="caption">
          Discover recent Dashboards from your teammates
        </Typography>
      </Stack>
      {renderDashboardList()}
    </Stack>
  );
};
